import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ExternalLink, Star, ArrowRight } from "lucide-react";
import { getSharedPersona } from "../lib/api";
import Logo from "../components/Logo";

function personaLeadIn(data) {
  if (data.handle) return `@${data.handle} is`;
  if (data.display_name) return `${data.display_name} is`;
  return "This viewer is";
}

export default function PersonaShared() {
  const { share_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try { setData(await getSharedPersona(share_id)); }
      catch (err) { setError(err?.response?.data?.detail || "Not found"); }
      finally { setLoading(false); }
    })();
  }, [share_id]);

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" /></div>;

  if (error || !data) {
    return (
      <div className="mx-auto max-w-[1200px] px-6 py-24 md:px-10 text-center">
        <h1 className="font-display text-6xl uppercase text-white">Not found</h1>
        <p className="mt-3 text-neutral-500">{error || "This persona link no longer exists."}</p>
        <Link to="/" className="mt-6 inline-block border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black">Try Otaku Index</Link>
      </div>
    );
  }

  const persona = data.persona;
  const totalRatings = Object.values(persona.genre_breakdown || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="relative mx-auto max-w-[1400px] px-6 py-14 md:px-10">
      <div className="pointer-events-none absolute inset-0 radiating opacity-40" />
      <div className="relative">
        {/* Branded ribbon */}
        <div className="flex items-center justify-between border-b border-white/10 pb-6">
          <div className="flex items-center gap-3">
            <Logo className="h-7 md:h-8" />
            <span className="hidden font-mono text-[10px] uppercase tracking-widest text-neutral-500 md:inline">Shared Persona</span>
          </div>
          <Link
            to="/"
            data-testid="cta-track-yours"
            className="flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-[#FF3B30]"
          >
            Track Yours <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="mt-10">
          <p className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">
            {personaLeadIn(data)}
          </p>
          <h1 data-testid="shared-persona-title" className="mt-2 font-display text-[3.5rem] uppercase leading-[0.9] tracking-tighter text-white md:text-[7rem]">
            {persona.personality_type}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-300">
            {persona.core_description}
          </p>
          {data.handle && (
            <p data-testid="shared-handle" className="mt-3 inline-block border border-[#FF3B30]/40 bg-[#FF3B30]/5 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-[#FF3B30]">
              @{data.handle}
            </p>
          )}
        </div>

        <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 md:grid-cols-4">
          <Cell label="Top Genre" value={persona.top_genre} accent />
          <Cell label="Avg Rating" value={persona.average_rating_given} />
          <Cell label="Completion" value={`${persona.completion_rate_percent}%`} />
          <Cell label="Anime" value={persona.total_anime_in_catalogue} />
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="border-l-2 border-[#FF3B30] pl-4">
            <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Rating Style</p>
            <p className="mt-1 text-sm text-white">{persona.rating_style}</p>
          </div>
          <div className="border-l-2 border-white/30 pl-4">
            <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Completion Style</p>
            <p className="mt-1 text-sm text-white">{persona.completion_style}</p>
          </div>
        </div>

        {/* Signature shows */}
        {data.signature_shows?.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display text-3xl uppercase tracking-widest text-white">Signature Shows</h2>
            <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              {data.signature_shows.map((s) => (
                <div key={s.mal_id} className="border border-white/10 bg-neutral-950">
                  <div className="relative aspect-[2/3] overflow-hidden bg-neutral-900">
                    {s.image_url && <img src={s.image_url} alt={s.title} loading="lazy" className="h-full w-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                    {s.user_rating != null && (
                      <div className="absolute right-2 top-2 flex items-center gap-1 border border-white/20 bg-black/70 px-2 py-1 font-mono text-[10px] text-white backdrop-blur">
                        <Star className="h-3 w-3 fill-[#FF3B30] text-[#FF3B30]" />
                        {Number(s.user_rating).toFixed(1)}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="line-clamp-2 text-sm font-semibold text-white">{s.title}</p>
                    <p className="mt-1 truncate font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                      {(s.genres || []).slice(0, 2).join(" · ")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Genre map */}
        <section className="mt-14">
          <h2 className="font-display text-3xl uppercase tracking-widest text-white">Genre Map</h2>
          <div className="mt-6 space-y-2">
            {Object.entries(persona.genre_breakdown || {}).slice(0, 15).map(([g, c]) => {
              const pct = totalRatings ? (c / totalRatings) * 100 : 0;
              return (
                <div key={g} className="border border-white/10 bg-neutral-950 px-4 py-3">
                  <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-widest">
                    <span className="text-white">{g}</span>
                    <span className="text-neutral-500">{c} · {pct.toFixed(1)}%</span>
                  </div>
                  <div className="mt-2 h-1 w-full bg-white/5">
                    <div className="h-full bg-[#FF3B30]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="mt-16 border-t border-white/10 pt-8 text-center">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Snapshot generated {data.created_at?.slice(0, 10)}
          </p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
          >
            Build your own persona <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div className={`bg-neutral-950 p-5 ${accent ? "border-l-2 border-[#FF3B30]" : ""}`}>
      <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-2 font-display text-3xl uppercase text-white md:text-4xl">{value}</p>
    </div>
  );
}
